import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Instagram, Linkedin, ArrowUpRight } from "lucide-react";

const ARRONDISSEMENTS = Array.from({ length: 20 }, (_, i) => `Paris ${i + 1}${i === 0 ? "ᵉʳ" : "ᵉ"}`);
const COMMUNES = [
  "Neuilly-sur-Seine", "Boulogne-Billancourt", "Levallois-Perret",
  "Saint-Mandé", "Vincennes", "Versailles", "Saint-Cloud",
  "Issy-les-Moulineaux", "Sèvres", "Suresnes", "Courbevoie",
  "Saint-Germain-en-Laye", "Maisons-Laffitte", "Le Vésinet",
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("Inscription confirmée. Bienvenue.");
      setEmail("");
    } catch (err) {
      toast.error("Email invalide ou déjà inscrit.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-coal text-bone">
      <div className="container-max pt-24 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="font-display text-6xl md:text-7xl leading-none">
              Maison
              <br />
              <span className="text-terracotta italic">Faubourg</span>
            </div>
            <p className="mt-8 text-bone/70 max-w-md leading-relaxed">
              Atelier de rénovation d'appartement, Paris &amp; Île-de-France.
              Une approche d'architecte, un savoir-faire d'artisan.
            </p>

            <form onSubmit={subscribe} data-testid="footer-newsletter-form" className="mt-10 max-w-md">
              <label className="overline text-bone/60">Recevoir le journal</label>
              <div className="flex border-b border-bone/40 mt-3">
                <input
                  data-testid="footer-newsletter-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="vous@email.fr"
                  className="bg-transparent flex-1 py-3 outline-none text-bone placeholder:text-bone/40"
                />
                <button
                  data-testid="footer-newsletter-submit"
                  disabled={loading}
                  className="overline text-terracotta hover:text-bone transition-colors"
                >
                  S'inscrire <ArrowUpRight className="inline w-3 h-3" />
                </button>
              </div>
            </form>
          </div>

          <div className="md:col-span-2">
            <p className="overline text-bone/50">Atelier</p>
            <ul className="mt-6 space-y-3 text-bone/85">
              <li><a href="/#manifeste" className="link-arc">Manifeste</a></li>
              <li><a href="/#methode" className="link-arc">Méthode</a></li>
              <li><a href="/#projets" className="link-arc">Projets</a></li>
              <li><Link to="/journal" className="link-arc">Journal</Link></li>
              <li><a href="/#contact" className="link-arc">Contact</a></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="overline text-bone/50">Services</p>
            <ul className="mt-6 space-y-3 text-bone/85">
              <li><Link to="/services/renovation-complete" className="link-arc">Rénovation complète</Link></li>
              <li><Link to="/services/renovation-cuisine" className="link-arc">Cuisine</Link></li>
              <li><Link to="/services/renovation-salle-de-bain" className="link-arc">Salle de bain</Link></li>
              <li><Link to="/services/peinture-decoration" className="link-arc">Peinture</Link></li>
              <li><Link to="/services/electricite" className="link-arc">Électricité</Link></li>
              <li><Link to="/services/plomberie" className="link-arc">Plomberie</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="overline text-bone/50">Atelier</p>
            <address className="not-italic mt-6 text-bone/85 leading-relaxed">
              27 rue du Faubourg Saint-Honoré<br />
              75008 Paris<br /><br />
              <a href="tel:+33184801234" className="link-arc">01 84 80 12 34</a><br />
              <a href="mailto:atelier@maison-faubourg.fr" className="link-arc">atelier@maison-faubourg.fr</a>
            </address>
            <div className="flex gap-4 mt-6">
              <a href="https://instagram.com" aria-label="Instagram" data-testid="footer-instagram" className="text-bone/70 hover:text-terracotta">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="https://linkedin.com" aria-label="LinkedIn" data-testid="footer-linkedin" className="text-bone/70 hover:text-terracotta">
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        {/* SEO local */}
        <div className="mt-20 border-t border-bone/15 pt-12">
          <p className="overline text-bone/50">Rénovation d'appartement à Paris — arrondissements desservis</p>
          <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-bone/60 text-sm">
            {ARRONDISSEMENTS.map((a) => (
              <span key={a}>Rénovation {a}</span>
            ))}
          </div>

          <p className="overline text-bone/50 mt-10">Île-de-France</p>
          <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-bone/60 text-sm">
            {COMMUNES.map((c) => (
              <span key={c}>Rénovation {c}</span>
            ))}
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-bone/15 flex flex-col md:flex-row md:items-center justify-between gap-4 text-bone/50 text-xs">
          <div>© {new Date().getFullYear()} MAISON FAUBOURG. Tous droits réservés.</div>
          <div className="flex gap-6">
            <Link to="/mentions-legales" className="link-arc">Mentions légales</Link>
            <span>SIRET 912 456 789 00012</span>
            <span>RGE Qualibat 1511</span>
          </div>
        </div>
      </div>

      {/* Massive watermark */}
      <div className="overflow-hidden">
        <div className="font-display text-[18vw] leading-none text-bone/[0.04] -mb-8 select-none px-6 whitespace-nowrap text-center">
          MAISON · FAUBOURG
        </div>
      </div>
    </footer>
  );
}
